import { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import logo from '../assets/eat_logo.png';

const NAV_LINKS = [
  { label: 'Home', to: '/' },
  {
    label: 'About',
    dropdown: [
      { label: 'About E.A.T.', to: '/about' },
      { label: 'Furqan Mestri, Founder', to: '/founder' },
    ],
  },
  { label: 'Services', to: '/services' },
  { label: 'Portfolio', to: '/portfolio' },
  {
    label: 'Our Programs',
    dropdown: [
      { label: 'CampusToCorp', to: '/program' },
      { label: 'IdeaToImpact', to: '/ideatoimpact' },
      { label: 'Project Assistance', to: '/project-assistance' },
    ],
  },
  { label: 'Careers', to: '/careers' },
  { label: 'FAQ', to: '/faq' },
  { label: 'Contact', to: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  // Active on a dropdown item's page and on its sub-pages (e.g. /ideatoimpact/apply).
  const isDropdownActive = (dropdown) =>
    dropdown.some((d) => location.pathname === d.to || location.pathname.startsWith(`${d.to}/`));

  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          background: scrolled ? 'rgba(26,26,46,0.98)' : 'rgba(26,26,46,0.95)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(0,174,239,0.12)',
          transition: 'all 0.3s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: 70,
            width: '100%',
            padding: '0 40px',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo */}
          <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img src={logo} alt="E.A.T." style={{ height: 36, width: 'auto', display: 'block' }} />
          </Link>

          {/* Desktop Nav */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 2 }}
            className="desktop-nav"
            ref={navRef}
          >
            {NAV_LINKS.map(({ label, to, dropdown }) =>
              dropdown ? (
                <div key={label} style={{ position: 'relative' }}>
                  <button
                    onClick={() => setOpenDropdown((o) => (o === label ? null : label))}
                    aria-haspopup="true"
                    aria-expanded={openDropdown === label}
                    style={{
                      color: isDropdownActive(dropdown) ? '#00AEEF' : 'rgba(255,255,255,0.75)',
                      fontFamily: "'Open Sans', sans-serif",
                      fontSize: 13,
                      fontWeight: 500,
                      padding: '8px 12px',
                      borderRadius: 8,
                      background: isDropdownActive(dropdown)
                        ? 'rgba(0,174,239,0.08)'
                        : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                    onMouseEnter={(e) => {
                      if (!isDropdownActive(dropdown)) e.currentTarget.style.color = '#fff';
                    }}
                    onMouseLeave={(e) => {
                      if (!isDropdownActive(dropdown))
                        e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                    }}
                  >
                    {label}{' '}
                    <span style={{ fontSize: 10, opacity: 0.7 }}>
                      {openDropdown === label ? '▲' : '▼'}
                    </span>
                  </button>
                  {openDropdown === label && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: 6,
                        background: '#1A1A2E',
                        border: '1px solid rgba(0,174,239,0.15)',
                        borderRadius: 10,
                        overflow: 'hidden',
                        minWidth: 200,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                        zIndex: 100,
                      }}
                    >
                      {dropdown.map(({ label: dlabel, to: dto }) => (
                        <NavLink
                          key={dto}
                          to={dto}
                          onClick={() => setOpenDropdown(null)}
                          style={({ isActive }) => ({
                            display: 'block',
                            padding: '11px 18px',
                            color: isActive ? '#00AEEF' : 'rgba(255,255,255,0.75)',
                            fontSize: 13,
                            fontWeight: 500,
                            textDecoration: 'none',
                            background: isActive ? 'rgba(0,174,239,0.08)' : 'transparent',
                            transition: 'all 0.2s',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            whiteSpace: 'nowrap',
                          })}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(0,174,239,0.08)';
                            e.currentTarget.style.color = '#00AEEF';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                          }}
                        >
                          {dlabel}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/'}
                  style={({ isActive }) => ({
                    color: isActive ? '#00AEEF' : 'rgba(255,255,255,0.75)',
                    fontFamily: "'Open Sans', sans-serif",
                    fontSize: 13,
                    fontWeight: 500,
                    padding: '8px 12px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    background: isActive ? 'rgba(0,174,239,0.08)' : 'transparent',
                    whiteSpace: 'nowrap',
                  })}
                  onMouseEnter={(e) => {
                    if (!e.currentTarget.classList.contains('active'))
                      e.currentTarget.style.color = '#fff';
                  }}
                  onMouseLeave={(e) => {
                    if (!e.currentTarget.classList.contains('active'))
                      e.currentTarget.style.color = 'rgba(255,255,255,0.75)';
                  }}
                >
                  {label}
                </NavLink>
              )
            )}
            <Link
              to="/register"
              onClick={() => setMobileOpen(false)}
              style={{
                textDecoration: 'none',
                display: 'inline-block',
                background: '#00AEEF',
                color: '#fff',
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 13,
                fontWeight: 700,
                padding: '10px 22px',
                border: 'none',
                borderRadius: 9,
                cursor: 'pointer',
                marginLeft: 8,
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#0090C8';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#00AEEF';
                e.currentTarget.style.transform = 'none';
              }}
            >
              Enroll Now
            </Link>
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setMobileOpen((o) => !o)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: 26,
              cursor: 'pointer',
              padding: 6,
              lineHeight: 1,
            }}
            className="ham-btn"
            aria-label="Toggle menu"
          >
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>
      </nav>

      {/* Mobile Nav Overlay */}
      {mobileOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(26,26,46,0.98)',
            zIndex: 999,
            display: 'flex',
            flexDirection: 'column',
            padding: '88px 32px 40px',
            gap: 4,
            overflowY: 'auto',
          }}
        >
          {NAV_LINKS.map(({ label, to, dropdown }) =>
            dropdown ? (
              <div key={label}>
                <p
                  style={{
                    color: 'rgba(255,255,255,0.4)',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    padding: '14px 0 6px',
                    borderBottom: '1px solid rgba(255,255,255,0.07)',
                  }}
                >
                  {label}
                </p>
                {dropdown.map(({ label: dlabel, to: dto }) => (
                  <NavLink
                    key={dto}
                    to={dto}
                    onClick={() => setMobileOpen(false)}
                    style={({ isActive }) => ({
                      color: isActive ? '#00AEEF' : 'rgba(255,255,255,0.7)',
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: 16,
                      fontWeight: 600,
                      padding: '10px 0 10px 16px',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                      textDecoration: 'none',
                      display: 'block',
                    })}
                  >
                    {dlabel}
                  </NavLink>
                ))}
              </div>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                onClick={() => setMobileOpen(false)}
                style={({ isActive }) => ({
                  color: isActive ? '#00AEEF' : 'rgba(255,255,255,0.8)',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 18,
                  fontWeight: 600,
                  padding: '14px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.07)',
                  textDecoration: 'none',
                  display: 'block',
                })}
              >
                {label}
              </NavLink>
            )
          )}
          <Link
            to="/register"
            onClick={() => setMobileOpen(false)}
            style={{
              textDecoration: 'none',
              display: 'inline-block',
              background: '#00AEEF',
              color: '#fff',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: 16,
              fontWeight: 700,
              padding: '16px 32px',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              marginTop: 20,
            }}
          >
            Enroll Now →
          </Link>
        </div>
      )}

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .ham-btn { display: block !important; }
        }
      `}</style>
    </>
  );
}
